import { isEquipmentBased } from "common";
import React, { SetStateAction } from "react";
import {
    AllowedPrivacy,
    DemographicPrice,
    Experience,
    PriceVariation,
    PricingGroup,
    PricingStructure,
    PricingTerm,
    PricingTermType,
    PricingType,
} from "types";
import {
    calculatePriceWithAdjustment,
    getDemographicPricesForExperienceCatalog,
} from "../../../models/DemographicPrice";
import { getEquipmentPricesForExperienceCatalog } from "../../../models/EquipmentPrice";
import { PriceVariationModal } from "./PriceVariationModal";
import { PriceVariationTable } from "./PriceVariationTable";

interface SchedulePriceVariationProps {
    experience: Experience;
    priceVariation: PriceVariation;
    showEditPriceVariationModal: boolean;
    setShowEditPriceVariationModal: (value: SetStateAction<boolean>) => void;
    onChange: (priceVariation: PriceVariation) => void;
}

const getPricingTerm = (terms: PricingTerm[], type: PricingTermType) => {
    return terms?.find((term) => term.type === type);
};

export const SchedulePriceVariation = ({
    experience,
    priceVariation,
    showEditPriceVariationModal,
    setShowEditPriceVariationModal,
    onChange,
}: SchedulePriceVariationProps) => {
    const getDemographicPricesWithAdjustments = (
        terms: PricingTerm[],
        pricingType = PricingType.PER_PERSON,
        pricingStructure = PricingStructure.FLAT,
        publicPricingGroup?: PricingGroup,
    ) => {
        const adjustedDemographicPrices: DemographicPrice[] = [];
        const isRelativePricing = pricingStructure === PricingStructure.RELATIVE;
        const isBuyoutPricing = pricingStructure === PricingStructure.BUYOUT;

        const publicPricingGroupTerms = [...(publicPricingGroup?.terms ?? [])];
        let baseTerm;
        let adjustmentTerm;

        // For relative pricing base price = base price of public + relative adjustment
        if (isRelativePricing) {
            // Public pricing base term
            const publicBaseTerm = getPricingTerm(publicPricingGroupTerms, PricingTermType.BASE);
            const adjustmentTerms = terms?.filter((term) => term.type === PricingTermType.ADJUSTMENT);

            // First adjustment term is the base term for relative pricing
            const relativeBaseTerm = { ...adjustmentTerms[0] };
            const relativeDemographicPrices = [...(relativeBaseTerm?.demographicPrices ?? [])];

            const updatedDemograpicPrices = [];
            for (const demographicPrice of relativeDemographicPrices) {
                const updatedPrice = { ...demographicPrice };
                const baseDemographicPrice = publicBaseTerm?.demographicPrices?.find((publicDemographicPrice) => {
                    return publicDemographicPrice.template.id === demographicPrice.template.id;
                });

                const basePrice = calculatePriceWithAdjustment(baseDemographicPrice?.price ?? 0, demographicPrice);
                updatedPrice.price = basePrice ?? 0;
                delete updatedPrice.adjustmentAmount;
                delete updatedPrice.adjustmentOperation;
                delete updatedPrice.adjustmentType;

                updatedDemograpicPrices.push(updatedPrice);
            }

            // Base price of relative pricing will have price calculated since, the adjustment amount of relative pricing will get overriden by
            // price variation adjustments, so we calculate actual price now and set it, and the adjustment values will have tiered pricing values
            baseTerm = {
                demographicPrices: updatedDemograpicPrices,
                type: PricingTermType.BASE,
            };
            adjustmentTerm = adjustmentTerms[adjustmentTerms.length - 1];
        } else if (isBuyoutPricing) {
            baseTerm = getPricingTerm(publicPricingGroupTerms, PricingTermType.BASE);
            const buyoutAdjustmentTerms = terms?.filter((term) => term.type === PricingTermType.ADJUSTMENT);
            adjustmentTerm = buyoutAdjustmentTerms?.[buyoutAdjustmentTerms.length - 1];
        } else {
            baseTerm = getPricingTerm(terms, PricingTermType.BASE);
            adjustmentTerm = getPricingTerm(terms, PricingTermType.ADJUSTMENT);
        }

        const baseDemographicPrices =
            terms.length > 0
                ? pricingType === PricingType.PER_PERSON
                    ? isEquipmentBased(experience)
                        ? getEquipmentPricesForExperienceCatalog(experience, terms[0])
                        : getDemographicPricesForExperienceCatalog(experience, terms[0])
                    : terms[0].demographicPrices
                : [];

        for (const demographic of baseDemographicPrices) {
            const idToMatch = demographic.template?.id;

            const matchingBaseTerm = baseTerm?.demographicPrices.find(
                (baseTermDemographic) => baseTermDemographic.template?.id === idToMatch,
            );

            const matchingAdjustmentTerm = adjustmentTerm?.demographicPrices.find(
                (adjustmentTermDemographic) => adjustmentTermDemographic.template?.id === idToMatch,
            );

            const adjustedDemographicPrice: DemographicPrice = {
                ...demographic,
                ...((isRelativePricing || isBuyoutPricing) && { price: matchingBaseTerm?.price }),
                adjustmentAmount: isRelativePricing
                    ? matchingAdjustmentTerm?.adjustmentAmount
                    : matchingBaseTerm?.adjustmentAmount ?? matchingAdjustmentTerm?.adjustmentAmount,
                adjustmentOperation: isRelativePricing
                    ? matchingAdjustmentTerm?.adjustmentOperation
                    : matchingBaseTerm?.adjustmentOperation ?? matchingAdjustmentTerm?.adjustmentOperation,
                adjustmentType: isRelativePricing
                    ? matchingAdjustmentTerm?.adjustmentType
                    : matchingBaseTerm?.adjustmentType ?? matchingAdjustmentTerm?.adjustmentType,
            };
            adjustedDemographicPrices.push(adjustedDemographicPrice);
        }

        return adjustedDemographicPrices;
    };

    const publicPricingGroup = priceVariation.pricingGroups.find((group) => group.privacy === AllowedPrivacy.PUBLIC);
    const updatedPriceVariation = {
        ...priceVariation,
        pricingGroups: priceVariation.pricingGroups.map((pricingGroup) => {
            return {
                ...pricingGroup,
                terms: [
                    {
                        demographicPrices: getDemographicPricesWithAdjustments(
                            pricingGroup.terms,
                            pricingGroup.pricingType,
                            pricingGroup.pricingStructure,
                            publicPricingGroup,
                        ),
                        type: PricingTermType.ADJUSTMENT,
                    },
                ],
            };
        }),
    };

    const updatedPublicPricingGroup = updatedPriceVariation.pricingGroups.find(
        (pricingGroup) => pricingGroup.privacy === AllowedPrivacy.PUBLIC,
    );
    const updatedPrivatePricingGroup = updatedPriceVariation.pricingGroups.find(
        (pricingGroup) => pricingGroup.privacy === AllowedPrivacy.PRIVATE,
    );
    const publicDemographicPricesWithAdjustments = updatedPublicPricingGroup
        ? updatedPublicPricingGroup.terms[0].demographicPrices
        : [];
    const privateDemographicPricesWithAdjustments = updatedPrivatePricingGroup
        ? updatedPrivatePricingGroup.terms[0].demographicPrices
        : [];

    const handleSubmit = (priceVariation: PriceVariation) => {
        onChange(priceVariation);
        setShowEditPriceVariationModal(false);
    };

    return (
        <>
            <div className="flex flex-col space-y-4">
                <PriceVariationTable
                    publicDemographicPrices={publicDemographicPricesWithAdjustments}
                    privateDemographicPrices={privateDemographicPricesWithAdjustments}
                    isPrivatePerOuting={updatedPrivatePricingGroup?.pricingType === PricingType.PER_OUTING}
                />
            </div>

            <PriceVariationModal
                isEditPriceVariation
                title="Edit Price Variation"
                isOpen={showEditPriceVariationModal}
                experience={experience}
                priceVariation={updatedPriceVariation}
                onClose={() => setShowEditPriceVariationModal(false)}
                onSubmit={handleSubmit}
            />
        </>
    );
};
